import { useEffect, useRef, useState, useCallback } from 'react';

let globalWs = null;
let globalListeners = new Set();
let globalConnected = false;
let reconnectTimeout = null;
let reconnectAttempts = 0;
const MAX_RECONNECT = 20;

function getWsUrl() {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

function connectGlobal() {
  if (globalWs && (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING)) {
    return;
  }

  const url = getWsUrl();
  if (!url) return;

  const ws = new WebSocket(url);
  globalWs = ws;

  ws.onopen = () => {
    globalConnected = true;
    reconnectAttempts = 0;
    globalListeners.forEach((l) => l._onConnect && l._onConnect());
  };

  ws.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    globalListeners.forEach((l) => l._onMessage && l._onMessage(data));
  };

  ws.onclose = () => {
    globalConnected = false;
    globalWs = null;
    if (reconnectTimeout) clearTimeout(reconnectTimeout);
    if (reconnectAttempts < MAX_RECONNECT) {
      reconnectAttempts++;
      const delay = Math.min(reconnectAttempts * 2000, 30000);
      reconnectTimeout = setTimeout(connectGlobal, delay);
    }
  };

  ws.onerror = () => {
    ws.close();
  };
}

export function useWebSocket(onMessage, options = {}) {
  const [connected, setConnected] = useState(globalConnected);
  const listenerRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const listener = {
      _onMessage: (data) => onMessageRef.current && onMessageRef.current(data),
      _onConnect: () => setConnected(true),
    };
    listenerRef.current = listener;
    globalListeners.add(listener);
    setConnected(globalConnected);

    connectGlobal();

    return () => {
      globalListeners.delete(listener);
    };
  }, []);

  const sendMessage = useCallback((message) => {
    if (globalWs && globalWs.readyState === WebSocket.OPEN) {
      const payload = typeof message === 'string' ? message : JSON.stringify(message);
      globalWs.send(payload);
    }
  }, []);

  const subscribe = useCallback((type, handler) => {
    const wrappedHandler = (data) => {
      if (data.type === type) handler(data);
    };
    const listener = {
      _onMessage: (data) => wrappedHandler(data),
    };
    globalListeners.add(listener);
    return () => globalListeners.delete(listener);
  }, []);

  return { connected, sendMessage, subscribe, reconnect: connectGlobal };
}

export default useWebSocket;
